"use client";

import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";
import { Input } from "@packages/components/ui/Input";
import Link from "@packages/components/ui/Link";
import { FOCUS_RING } from "@packages/design-system/constants";
import type { AppUserSession } from "@packages/lib/auth/types";
import { isMenuItemGroup } from "@packages/lib/menu-type-guards";
import type {
  MenuItem,
  MenuItemGroup,
  MenuItemLeaf,
} from "@packages/lib/types";
import { cn } from "@packages/lib/utils";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { Button } from "./ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/DropdownMenu";
import ThemeToggle from "./ui/ThemeToggle";

// Standardized navbar classes
const NAVBAR_DESKTOP_BUTTON_CLASSES =
  "flex h-full items-center border-b-2 px-1 pt-1 text-sm font-medium transition-colors text-foreground hover:text-accent-foreground hover:border-accent-foreground border-transparent";
const NAVBAR_DROPDOWN_MENU_ITEM_CLASSES =
  "text-foreground border-l-4 border-transparent px-4 py-2 font-medium hover:bg-secondary hover:text-secondary-foreground hover:border-border text-sm whitespace-nowrap";
const SHARED_ACTIVE_CLASSES =
  "text-heading border-accent-foreground hover:border-accent-foreground";
const NAVBAR_DESKTOP_BUTTON_ACTIVE_CLASSES = `${SHARED_ACTIVE_CLASSES} text-heading`;
const NAVBAR_DROPDOWN_MENU_ITEM_ACTIVE_CLASSES = `${SHARED_ACTIVE_CLASSES} bg-accent hover:bg-accent`;
const NAVBAR_DROPDOWN_DESKTOP_CLASSES =
  "absolute z-20 rounded-xl border bg-background border-border-light shadow-lg mt-0.5 overflow-hidden w-fit";

function NavBarSearch({
  router,
  showSearch,
}: {
  router: ReturnType<typeof useRouter>;
  showSearch: boolean;
}) {
  const searchParams = useSearchParams();
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (showSearch && searchParams) {
      const query = searchParams.get("query");
      if (query) {
        setSearch(query);
      }
    }
  }, [showSearch, searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams || "");
    params.set("query", search);
    router.push(`/search?${params.toString()}`);
  };

  return (
    <div className="ml-2 hidden w-full min-w-48 flex-1 items-center justify-center px-2 lg:ml-6 lg:flex lg:justify-end">
      <form
        onSubmit={handleSearch}
        className="grid w-full max-w-lg grid-cols-1 lg:max-w-xs"
        role="search"
        aria-label="Site"
      >
        <Input
          type="search"
          placeholder="Search videos..."
          className="bg-background text-input-label outline-border placeholder:text-input-placeholder focus:outline-ring col-start-1 row-start-1 block w-full rounded-md py-1.5 pr-3 pl-10 text-base outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search"
        />
        <svg
          className="text-muted-foreground pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          data-slot="icon"
        >
          <path
            fillRule="evenodd"
            d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
            clipRule="evenodd"
          />
        </svg>
      </form>
    </div>
  );
}

export function NavBar({
  session,
  mainMenu,
  userMenu,
  siteName,
  logoNavBarLight,
  logoNavBarDark,
  showSearch = false,
  logoHref = "/",
}: {
  session: AppUserSession;
  mainMenu: MenuItemGroup[];
  userMenu: MenuItemLeaf[];
  siteName: string;
  logoNavBarLight: { src: string; width: number; height: number };
  logoNavBarDark: { src: string; width: number; height: number };
  showSearch?: boolean;
  logoHref?: string;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});
  const router = useRouter();
  const pathname = usePathname();
  const parentKey = (label: string) => label;
  const childKey = (parent: string, child: string) => `${parent}::${child}`;

  const handleParentToggle = (label: string) => {
    setDropdownOpen((prev) => {
      const key = parentKey(label);
      const isOpen = !!prev[key];
      const next: Record<string, boolean> = {};
      // close other parents (keep child keys untouched for other parents)
      Object.keys(prev).forEach((k) => {
        if (!k.includes("::") && k !== key) next[k] = false;
      });
      if (isOpen) {
        // closing parent -> mark parent closed and remove its children keys
        const reduced = { ...prev, ...next, [key]: false };
        // clear children for this parent
        Object.keys(reduced).forEach((k) => {
          if (k.startsWith(`${key}::`)) {
            delete reduced[k];
          }
        });
        return reduced;
      }
      // opening parent -> ensure it is true and siblings are closed
      return { ...prev, ...next, [key]: true };
    });
  };

  const handleChildToggle = (parent: string, child: string) => {
    setDropdownOpen((prev) => {
      const pKey = parentKey(parent);
      const cKey = childKey(parent, child);
      const isOpen = !!prev[cKey];
      // ensure parent is open and toggle this child; close sibling children
      const next = { ...prev, [pKey]: true, [cKey]: !isOpen };
      Object.keys(prev).forEach((k) => {
        if (k.startsWith(`${pKey}::`) && k !== cKey) {
          next[k] = false;
        }
      });
      return next;
    });
  };

  const disableTheme = process.env.NEXT_PUBLIC_DISABLE_THEME === "true";

  return (
    // Safari (especially iOS) has poor performance with box-shadow on sticky/fixed elements.
    //  Use Tailwind arbitrary variant to target Mobile Safari at max-sm:
    // max-sm:[@supports(-webkit-touch-callout:none)]:border-b
    // max-sm:[@supports(-webkit-touch-callout:none)]:border-border-light
    // max-sm:[@supports(-webkit-touch-callout:none)]:shadow-none
    // This keeps shadow-sm by default but replaces it with a bottom border on mobile Safari only.

    <header className="bg-background max-sm:[@supports(-webkit-touch-callout:none)]:border-border-light dark:border-border-lightest shadow-sm dark:border-b max-sm:[@supports(-webkit-touch-callout:none)]:border-b max-sm:[@supports(-webkit-touch-callout:none)]:shadow-none">
      <div className="container px-2 sm:px-4 lg:px-8">
        {/* Reordered: mobile menu button moved to left, logo centered on mobile, user menu visible on right */}
        <div id="navbar" className="flex h-16 justify-between">
          {/* Mobile menu button on the left (visible only on small screens) */}
          <div className="flex items-center lg:hidden">
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  className="text-muted-foreground hover:bg-secondary hover:text-muted-heading relative inline-flex items-center justify-center rounded-none p-2"
                  aria-controls="mobile-menu"
                  aria-expanded={isMenuOpen}
                  variant="noStyles"
                >
                  <span className="absolute -inset-0.5"></span>
                  <span className="sr-only">
                    {isMenuOpen ? "Open main menu" : "Close main menu"}
                  </span>
                  <Bars3Icon
                    className={isMenuOpen ? "hidden size-6" : "block size-6"}
                    aria-hidden="true"
                  />
                  <XMarkIcon
                    className={isMenuOpen ? "block size-6" : "hidden size-6"}
                    aria-hidden="true"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={cn(
                  NAVBAR_DROPDOWN_DESKTOP_CLASSES,
                  // mobile-specific layout overrides: span most of the viewport width and add inner padding
                  "w-autod right-2 left-2 mt-2 p-2 lg:hidden",
                )}
              >
                <div className="lg:hidden" id="mobile-menu">
                  <>
                    <div className="space-y-1">
                      {mainMenu.map((item) =>
                        isMenuItemGroup(item) &&
                        item.items &&
                        item.items.length > 0 ? (
                          <div key={item.label}>
                            <Button
                              // prevent default so any accidental anchor behavior doesn't navigate
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                handleParentToggle(item.label);
                              }}
                              type="button"
                              className={`block w-full rounded-none text-left ${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} w-full text-base md:w-full`}
                              variant="noStyles"
                              aria-expanded={
                                !!dropdownOpen[parentKey(item.label)]
                              }
                            >
                              {item.label}
                            </Button>
                            {dropdownOpen[parentKey(item.label)] && (
                              <div className="pl-4">
                                {item.items.map((subItem) =>
                                  isMenuItemGroup(subItem) &&
                                  subItem.items &&
                                  subItem.items.length > 0 ? (
                                    <div
                                      key={item.label + "::" + subItem.label}
                                    >
                                      <Button
                                        onClick={(e: React.MouseEvent) => {
                                          e.preventDefault();
                                          handleChildToggle(
                                            item.label,
                                            subItem.label,
                                          );
                                        }}
                                        type="button"
                                        className={`block w-full rounded-none text-left ${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} w-full text-base md:w-full`}
                                        variant="noStyles"
                                        aria-expanded={
                                          !!dropdownOpen[
                                            childKey(item.label, subItem.label)
                                          ]
                                        }
                                      >
                                        {subItem.label}
                                      </Button>
                                      {dropdownOpen[
                                        childKey(item.label, subItem.label)
                                      ] && (
                                        <div className="pl-4">
                                          {subItem.items.map(
                                            (grand: MenuItem) =>
                                              isMenuItemGroup(grand) ? null : (
                                                <DropdownMenuItem
                                                  key={
                                                    item.label +
                                                    subItem.label +
                                                    grand.label
                                                  }
                                                  asChild
                                                >
                                                  <Link
                                                    href={grand.href as string}
                                                    className={`block ${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} text-base ${
                                                      pathname === grand.href
                                                        ? NAVBAR_DROPDOWN_MENU_ITEM_ACTIVE_CLASSES
                                                        : ""
                                                    }`}
                                                  >
                                                    {grand.label}
                                                  </Link>
                                                </DropdownMenuItem>
                                              ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // leaf child: render as link
                                    <DropdownMenuItem
                                      key={item.label + subItem.label}
                                      asChild
                                    >
                                      <Link
                                        href={subItem.href as string}
                                        className={`block ${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} text-base ${
                                          pathname === subItem.href
                                            ? NAVBAR_DROPDOWN_MENU_ITEM_ACTIVE_CLASSES
                                            : ""
                                        }`}
                                      >
                                        {subItem.label}
                                      </Link>
                                    </DropdownMenuItem>
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          // no children: render as link (use existing href)
                          <DropdownMenuItem key={item.label} asChild>
                            <Link
                              href={item.href as string}
                              className={`block ${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} text-base ${
                                (item.href === "/videos" &&
                                  pathname?.startsWith("/videos")) ||
                                pathname === item.href
                                  ? NAVBAR_DROPDOWN_MENU_ITEM_ACTIVE_CLASSES
                                  : ""
                              }`}
                            >
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        ),
                      )}
                    </div>
                  </>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Logo centered on mobile */}
          <div className="flex shrink-0 items-center max-lg:flex-1 max-lg:justify-center">
            <Link href={logoHref}>
              <span className="sr-only">{siteName}</span>
              <Image
                alt={`${siteName} logo`}
                src={logoNavBarLight.src}
                className="block h-8 w-auto dark:hidden"
                width={logoNavBarLight.width}
                height={logoNavBarLight.height}
                aria-hidden="true"
                priority
              />
              <Image
                alt={`${siteName} logo`}
                src={logoNavBarDark.src}
                className="hidden h-8 w-auto dark:block"
                width={logoNavBarDark.width}
                height={logoNavBarDark.height}
                aria-hidden="true"
                priority
              />
            </Link>
          </div>

          {/* --- Desktop Navigation using Radix NavigationMenu (unchanged) --- */}
          <>
            {/* --- Desktop Navigation using Radix NavigationMenu --- */}
            <NavigationMenuPrimitive.Root className="hidden h-full items-stretch lg:ml-6 lg:flex lg:w-full lg:justify-center">
              <NavigationMenuPrimitive.List className="h-full items-stretch lg:flex lg:space-x-8">
                {mainMenu.map(
                  (item) =>
                    (!item.requiresAdmin || session.userFlags.isAdmin) &&
                    (isMenuItemGroup(item) &&
                    item.items &&
                    item.items.length > 0 ? (
                      <NavigationMenuPrimitive.Item
                        key={item.label}
                        className="relative h-full"
                      >
                        <NavigationMenuPrimitive.Trigger
                          className={cn(
                            FOCUS_RING,
                            NAVBAR_DESKTOP_BUTTON_CLASSES,
                            item.items.some(
                              (dropdownItem) => pathname === dropdownItem.href,
                            )
                              ? NAVBAR_DESKTOP_BUTTON_ACTIVE_CLASSES
                              : "",
                          )}
                          aria-label={`${item.label} menu`}
                        >
                          {item.label}
                        </NavigationMenuPrimitive.Trigger>
                        <NavigationMenuPrimitive.Content
                          className={cn(
                            // For mega menus, position fixed to viewport so it centers on screen.
                            item.items.some(
                              (s) =>
                                isMenuItemGroup(s) &&
                                s.items &&
                                s.items.length > 0,
                            )
                              ? "fixed top-16 right-0 left-0 z-20 overflow-visible bg-transparent shadow-none"
                              : NAVBAR_DROPDOWN_DESKTOP_CLASSES,
                            // keep the centered min-width translate only for the non-mega case
                            item.items.some(
                              (s) =>
                                isMenuItemGroup(s) &&
                                s.items &&
                                s.items.length > 0,
                            )
                              ? ""
                              : "left-1/2 min-w-fit -translate-x-1/2",
                          )}
                        >
                          {item.items.some(
                            (s) =>
                              isMenuItemGroup(s) &&
                              s.items &&
                              s.items.length > 0,
                          ) ? (
                            <div className="pointer-events-auto w-full">
                              {/* Presentational short shadow element (prevents shadow poking at top) */}
                              <div
                                aria-hidden
                                className="bg-background absolute inset-0 top-1/2 shadow-sm"
                              />
                              <div className="bg-background relative">
                                <div className="mx-auto max-w-7xl px-8">
                                  <div className="grid grid-cols-2 items-start gap-x-8 gap-y-10 pt-6 pb-8">
                                    {(() => {
                                      const subs = item.items;
                                      const half = Math.ceil(subs.length / 2);
                                      const leftGroup = subs.slice(0, half);
                                      const rightGroup = subs.slice(half);
                                      const renderGroup = (
                                        group: MenuItem[],
                                        groupIdx: number,
                                      ) => (
                                        <div
                                          className="grid grid-cols-2 gap-x-8 gap-y-10"
                                          key={groupIdx}
                                        >
                                          {group.map((sub) =>
                                            isMenuItemGroup(sub) ? (
                                              <div key={sub.label}>
                                                <Link
                                                  id={`desktop-${item.label
                                                    .toLowerCase()
                                                    .replace(
                                                      /\s+/g,
                                                      "-",
                                                    )}-${sub.label
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "-")}`}
                                                  className="text-heading text-sm font-semibold"
                                                  href={sub.href as string}
                                                >
                                                  {sub.label}
                                                </Link>

                                                <ul
                                                  role="list"
                                                  aria-labelledby={`desktop-${item.label
                                                    .toLowerCase()
                                                    .replace(
                                                      /\s+/g,
                                                      "-",
                                                    )}-${sub.label
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "-")}`}
                                                  className="mt-4 space-y-4"
                                                >
                                                  {sub.items.map(
                                                    (child: MenuItem) =>
                                                      isMenuItemGroup(
                                                        child,
                                                      ) ? null : (
                                                        <li
                                                          className="flex"
                                                          key={child.label}
                                                        >
                                                          <Link
                                                            href={
                                                              child.href as string
                                                            }
                                                            className="hover:text-link-gray-hover text-link-gray text-sm font-medium"
                                                            noDefaultColor
                                                          >
                                                            {child.label}
                                                          </Link>
                                                        </li>
                                                      ),
                                                  )}
                                                </ul>
                                              </div>
                                            ) : null,
                                          )}
                                        </div>
                                      );
                                      return (
                                        <>
                                          {renderGroup(leftGroup, 0)}
                                          {renderGroup(rightGroup, 1)}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {item.items.map((subItem: MenuItem) =>
                                isMenuItemGroup(subItem) ? null : (
                                  <Link
                                    key={subItem.href}
                                    href={subItem.href as string}
                                    noDefaultColor
                                    className={`block grow ${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} ${
                                      pathname === subItem.href
                                        ? NAVBAR_DROPDOWN_MENU_ITEM_ACTIVE_CLASSES
                                        : ""
                                    }`}
                                  >
                                    {subItem.label}
                                  </Link>
                                ),
                              )}
                            </div>
                          )}
                        </NavigationMenuPrimitive.Content>
                      </NavigationMenuPrimitive.Item>
                    ) : (
                      item.href && (
                        <NavigationMenuPrimitive.Item
                          key={item.label}
                          className="h-full"
                        >
                          <Link
                            href={item.href as string}
                            className={cn(
                              NAVBAR_DESKTOP_BUTTON_CLASSES,
                              pathname === item.href
                                ? NAVBAR_DESKTOP_BUTTON_ACTIVE_CLASSES
                                : "",
                            )}
                          >
                            {item.label}
                          </Link>
                        </NavigationMenuPrimitive.Item>
                      )
                    )),
                )}
              </NavigationMenuPrimitive.List>
            </NavigationMenuPrimitive.Root>
            {/* --- End Desktop Navigation --- */}
            {showSearch && (
              <Suspense fallback={null}>
                <NavBarSearch router={router} showSearch={showSearch} />
              </Suspense>
            )}
          </>

          {/* User Menu visible on mobile and desktop (keeps existing content, now placed to the right) */}
          <div className="ml-4 flex items-center">
            <div className="relative ml-4 shrink-0">
              <NavigationMenuPrimitive.Root>
                <NavigationMenuPrimitive.List>
                  <NavigationMenuPrimitive.Item>
                    <NavigationMenuPrimitive.Trigger asChild>
                      <Button
                        variant="ghost"
                        size="none"
                        className="relative size-8 overflow-hidden rounded-full"
                      >
                        <Image
                          alt={`${session.user.username}'s avatar`}
                          src={session.user.picture}
                          className="bg-secondary h-full w-full object-cover"
                          height={100}
                          width={100}
                          priority
                        />
                        <span className="sr-only">Open user menu</span>
                      </Button>
                    </NavigationMenuPrimitive.Trigger>
                    <NavigationMenuPrimitive.Content
                      className={cn(
                        NAVBAR_DROPDOWN_DESKTOP_CLASSES,
                        "divide-border-light origin-top-right divide-y transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in",
                      )}
                      style={{ right: 0, left: "auto" }}
                    >
                      <div className="cursor-default px-4 py-3">
                        <p className="text-foreground text-sm">Signed in as</p>
                        <p className="text-heading truncate text-sm font-medium">
                          {session.user.email}
                        </p>
                      </div>
                      <div>
                        {userMenu.map((link) => (
                          <div key={link.label}>
                            <Link
                              href={link.href}
                              className={`${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} block grow text-sm`}
                              noDefaultColor
                            >
                              {link.label}
                            </Link>
                          </div>
                        ))}
                      </div>
                      <form action="#" method="POST">
                        <Link
                          className={`${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} block grow text-sm`}
                          href={"/auth/logout"}
                          prefetch={false}
                          noDefaultColor
                        >
                          Sign out
                        </Link>
                      </form>
                      {!disableTheme && (
                        <div
                          className={`${NAVBAR_DROPDOWN_MENU_ITEM_CLASSES} grow text-sm`}
                        >
                          <ThemeToggle />
                        </div>
                      )}
                    </NavigationMenuPrimitive.Content>
                  </NavigationMenuPrimitive.Item>
                </NavigationMenuPrimitive.List>
              </NavigationMenuPrimitive.Root>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
